/**
 * The rail itself: one big button per TOP-LEVEL navigation entry (icon + its
 * title), "All screens" at the top and Open editors (with its count) at the
 * foot. A folder (or a website with pages) discloses the fly-out panel; a
 * top-level screen opens directly. How deep a folder goes is the panel's
 * business, never the rail's.
 *
 * Every button is a real <button>, ≥ 60 × 74 (the 44px touch floor with
 * room); Up / Down / Home / End move between them, Right steps into the open
 * panel. Labels are clamped to two lines, so the full title is also shown as a
 * tooltip on hover and on keyboard focus — drawn `position: fixed`, because
 * the scrolling middle section would clip anything absolutely positioned.
 */
import { LayoutGrid, Layers } from 'lucide-react'
import { useCallback, useState, type CSSProperties, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { useEntryMenu } from '../../nav/parts/ShellLists'
import { NavIcon } from '../../nav/navIcons'
import { S } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { ShellNavModel } from '../../nav/useShellNav'
import { R } from './tokens'

/** What the fly-out panel is showing. */
export type RailPanel =
  | { kind: 'folder'; id: string }
  | { kind: 'all' }
  | { kind: 'editors' }

/** A top-level entry that opens the panel rather than a screen. */
export function opensPanel(n: NavTreeNode): boolean {
  return n.isFolder || (n.isContainer && n.children.length > 0)
}

export interface RailBarProps {
  model: ShellNavModel
  accent: string
  compact: boolean
  /** The panel on show, or null when the fly-out is closed. */
  shown: RailPanel | null
  /** Id of the top-level entry the current screen lives under. */
  currentRootId: string | null
  flyoutId: string
  /** Toggle the panel for this view; `opener` is where focus returns on Esc. */
  onPanel: (panel: RailPanel, opener: HTMLElement) => void
  /** Right arrow on a button whose panel is open: move into the panel. */
  onEnterPanel: () => void
}

interface Tip { text: string; top: number; left: number }

export function RailBar({
  model, accent, compact, shown, currentRootId, flyoutId, onPanel, onEnterPanel,
}: RailBarProps) {
  const entryMenu = useEntryMenu(model)
  const [tip, setTip] = useState<Tip | null>(null)
  const width = compact ? 76 : 88
  const btnWidth = compact ? 64 : 74

  const showTip = useCallback((el: HTMLElement, text: string) => {
    const r = el.getBoundingClientRect()
    setTip({ text, top: r.top + r.height / 2, left: r.right + 10 })
  }, [])
  const hideTip = useCallback(() => setTip(null), [])
  // Focus shows the tip only for the keyboard: a mouse click focuses too, and
  // a tip left over the panel it just opened would cover the panel's title.
  const onFocusTip = (text: string) => (e: FocusEvent<HTMLElement>) => {
    let keyboard = true
    try { keyboard = e.currentTarget.matches(':focus-visible') } catch { /* old engine: show it */ }
    if (keyboard) showTip(e.currentTarget, text)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLElement
    if (!target.dataset.railBtn) return
    const els = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-rail-btn]')]
    const i = els.indexOf(target)
    const go = (j: number) => { e.preventDefault(); els[Math.max(0, Math.min(els.length - 1, j))]?.focus() }
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break
      case 'ArrowUp': go(i - 1); break
      case 'Home': go(0); break
      case 'End': go(els.length - 1); break
      case 'ArrowRight':
        if (target.getAttribute('aria-expanded') === 'true') { e.preventDefault(); onEnterPanel() }
        break
      case 'Escape': hideTip(); break
      default:
    }
  }

  const button = (opts: {
    key: string
    label: string
    icon: ReactNode
    expanded?: boolean
    current?: boolean
    marked?: boolean
    badge?: number
    ariaLabel?: string
    hooks: Record<string, string>
    onClick: (el: HTMLElement) => void
    onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void
    onKey?: (e: KeyboardEvent<HTMLElement>) => void
    controls?: boolean
  }) => {
    const on = !!opts.expanded || !!opts.current
    return (
      <button
        key={opts.key}
        type="button"
        data-rail-btn="1"
        className="tw-rail-btn"
        aria-label={opts.ariaLabel}
        aria-expanded={opts.controls ? !!opts.expanded : undefined}
        aria-controls={opts.controls && opts.expanded ? flyoutId : undefined}
        aria-current={opts.current ? 'page' : undefined}
        {...opts.hooks}
        onClick={(e) => { hideTip(); opts.onClick(e.currentTarget) }}
        onContextMenu={opts.onContextMenu}
        onKeyDown={opts.onKey}
        onMouseEnter={(e) => showTip(e.currentTarget, opts.ariaLabel || opts.label)}
        onMouseLeave={hideTip}
        onFocus={onFocusTip(opts.ariaLabel || opts.label)}
        onBlur={hideTip}
        style={{
          position: 'relative', flexShrink: 0, width: btnWidth, minHeight: compact ? 54 : 60,
          padding: compact ? '7px 2px 6px' : '9px 2px 8px', border: 'none', borderRadius: 12,
          background: on ? R.active : 'transparent', color: on ? R.activeText : R.text,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {/* The bar marks the folder the screen on display lives in. */}
        <span aria-hidden style={{
          position: 'absolute', left: -((width - btnWidth) / 2) + 1, top: 14, bottom: 14, width: 3, borderRadius: 3,
          background: opts.marked ? accent : 'transparent',
        }} />
        <span aria-hidden style={{ display: 'inline-flex' }}>{opts.icon}</span>
        <span style={labelStyle}>{opts.label}</span>
        {typeof opts.badge === 'number' && opts.badge > 0 && (
          <span aria-hidden style={{
            position: 'absolute', top: 3, right: compact ? 6 : 10, minWidth: 20, height: 20, padding: '0 5px',
            boxSizing: 'border-box', borderRadius: 10, background: accent, color: '#fff',
            fontSize: 13, fontWeight: 700, lineHeight: '20px', textAlign: 'center',
          }}>{opts.badge}</span>
        )}
      </button>
    )
  }

  const allShown = shown?.kind === 'all'
  const editorsShown = shown?.kind === 'editors'
  const n = model.editors.length

  return (
    <nav aria-label="Top-level folders" onKeyDown={onKeyDown}
      {...tw('action', 'shell-rail')}
      style={{
        width, flexShrink: 0, background: R.bg, color: R.text,
        display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 0,
        padding: compact ? '10px 0 10px' : '14px 0 12px',
      }}>
      {/* All screens — the whole menu at once, any depth. Styled as the
          board's app mark: the host's top bar already names the app. */}
      <button
        type="button"
        data-rail-btn="1"
        className="tw-rail-mark"
        aria-label="All screens"
        aria-expanded={allShown}
        aria-controls={allShown ? flyoutId : undefined}
        {...tw('action', 'shell-rail-all', { selected: allShown })}
        onClick={(e) => { hideTip(); onPanel({ kind: 'all' }, e.currentTarget) }}
        onMouseEnter={(e) => showTip(e.currentTarget, 'All screens')}
        onMouseLeave={hideTip}
        onFocus={onFocusTip('All screens')}
        onBlur={hideTip}
        style={{
          width: 44, height: 44, flexShrink: 0, marginBottom: compact ? 10 : 14, padding: 0, border: 'none',
          borderRadius: 11, background: accent, color: '#fff', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: allShown ? `0 0 0 3px ${R.active}, 0 0 0 5px ${accent}` : 'none',
        }}
      >
        <LayoutGrid size={21} aria-hidden />
      </button>

      <div className="tw-rail-scroll" style={{
        flex: 1, minHeight: 0, width: '100%', overflowY: 'auto', overflowX: 'hidden',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '2px 0',
      }}>
        {model.roots.map((node) => {
          const item = node.item
          const panel = opensPanel(node)
          const expanded = panel && shown?.kind === 'folder' && shown.id === item.id
          const current = !panel && model.current?.id === item.id
          return button({
            key: item.id,
            label: item.title,
            icon: <NavIcon node={item} size={22} />,
            controls: panel,
            expanded,
            current,
            marked: currentRootId === item.id,
            hooks: tw('nav', item.obj_name || item.title, {
              type: item.obj_type, folder: node.isFolder, selected: expanded || current, rail: true,
            }),
            onClick: (el) => {
              if (panel) onPanel({ kind: 'folder', id: item.id }, el)
              else model.open(item)
            },
            // A top-level screen offers Pin / Unpin like any menu entry.
            onContextMenu: panel && node.isFolder ? undefined : entryMenu.onContextMenu(item),
            onKey: panel && node.isFolder ? undefined : entryMenu.onKeyDown(item),
          })
        })}
      </div>

      {model.editorsEnabled && (
        <div style={{ flexShrink: 0, paddingTop: 8, marginTop: 6, borderTop: `1px solid ${R.divider}`,
          width: btnWidth, display: 'flex', justifyContent: 'center' }}>
          {button({
            key: 'editors',
            label: 'Editors',
            icon: <Layers size={21} />,
            controls: true,
            expanded: editorsShown,
            badge: n,
            ariaLabel: n ? `Open editors, ${n} open` : 'Open editors',
            hooks: tw('action', 'shell-rail-editors', { selected: editorsShown, count: n }),
            onClick: (el) => onPanel({ kind: 'editors' }, el),
          })}
        </div>
      )}
      {entryMenu.ui}
      {tip && (
        <span aria-hidden style={{
          position: 'fixed', top: tip.top, left: tip.left, transform: 'translateY(-50%)', zIndex: 9430,
          maxWidth: 280, padding: '6px 10px', borderRadius: 8, pointerEvents: 'none',
          background: S.text, color: S.panel, fontSize: 13, lineHeight: 1.4, fontWeight: 500,
          boxShadow: '0 6px 18px rgba(0,0,0,0.22)', whiteSpace: 'normal',
        } as CSSProperties}>
          {tip.text}
        </span>
      )}
    </nav>
  )
}

const labelStyle: CSSProperties = {
  fontSize: 13, fontWeight: 500, lineHeight: 1.15, textAlign: 'center', maxWidth: '100%',
  // Break between words; a word too long for the button is hyphenated
  // ("Manage-ment") rather than split bare or clipped.
  padding: '0 2px', boxSizing: 'border-box', overflow: 'hidden', wordBreak: 'normal',
  overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
}
